import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { 
  Box, 
  Typography, 
  IconButton, 
  Button,
  Divider,
  Grid
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import Card from './Card';

/**
 * ProfileCard component for displaying user information in a card format
 * with optional editing capabilities
 */
const ProfileCard = ({
  title,
  icon,
  children,
  editable = false,
  onSave,
  color = 'primary',
  ...props
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [originalData, setOriginalData] = useState(null);

  const handleEditClick = () => {
    // Store original data for cancel operation
    if (onSave && typeof onSave === 'function') {
      setOriginalData(onSave('get'));
    }
    setIsEditing(true);
  };

  const handleSaveClick = () => {
    if (onSave && typeof onSave === 'function') {
      onSave('save');
    }
    setIsEditing(false);
    setOriginalData(null);
  };

  const handleCancelClick = () => {
    if (onSave && typeof onSave === 'function' && originalData) {
      onSave('cancel', originalData);
    }
    setIsEditing(false);
    setOriginalData(null);
  };

  // Action button for the card header
  const actionButton = editable ? (
    isEditing ? (
      <Box sx={{ display: 'flex' }}>
        <IconButton 
          size="small" 
          onClick={handleCancelClick}
          aria-label="cancel"
          sx={{ mr: 1 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <IconButton 
          size="small" 
          color="primary" 
          onClick={handleSaveClick}
          aria-label="save"
        >
          <SaveIcon fontSize="small" />
        </IconButton>
      </Box>
    ) : (
      <IconButton 
        size="small" 
        color="primary" 
        onClick={handleEditClick}
        aria-label="edit"
      >
        <EditIcon fontSize="small" />
      </IconButton>
    )
  ) : null;

  // Avatar for the card header
  const avatarIcon = icon ? (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: `${color}.light`,
        color: `${color}.main`,
      }}
    >
      {icon}
    </Box>
  ) : null;

  return (
    <Card
      title={title}
      avatar={avatarIcon}
      action={actionButton}
      elevation={1}
      {...props}
    >
      <Box sx={{ position: 'relative' }}>
        {/* Pass isEditing state to children if they're a function */}
        {typeof children === 'function' ? children(isEditing) : children}
      </Box>
    </Card>
  );
};

/**
 * ProfileField component for displaying a field in a ProfileCard
 */
export const ProfileField = ({ label, value, editing, children }) => {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      
      {editing && children ? (
        children
      ) : (
        <Typography variant="body1">
          {value || '—'}
        </Typography>
      )}
    </Box>
  );
};

/**
 * ProfileSection component for grouping related fields in a ProfileCard
 */
export const ProfileSection = ({ title, children, divider = true }) => {
  return (
    <Box sx={{ mb: 3 }}>
      {title && (
        <Typography 
          variant="subtitle1" 
          sx={{ 
            fontWeight: 600, 
            mb: 2,
            color: 'primary.main'
          }}
        >
          {title}
        </Typography>
      )}
      
      <Grid container spacing={2}>
        {children}
      </Grid>
      
      {divider && <Divider sx={{ mt: 3 }} />}
    </Box>
  );
};

ProfileCard.propTypes = {
  title: PropTypes.node.isRequired,
  icon: PropTypes.node,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  editable: PropTypes.bool,
  onSave: PropTypes.func,
  color: PropTypes.string,
};

ProfileField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
  editing: PropTypes.bool,
  children: PropTypes.node,
};

ProfileSection.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
  divider: PropTypes.bool,
};

export default ProfileCard;
